import type { Invoice } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

/**
 * What the statement still owes, as materialized on the Invoice.
 *
 * The balance-due extension is rewritten in the same transaction as every posting made against the
 * statement, so it is the authoritative remaining figure wherever it is present.
 *
 * Absent on a statement written before the extension existed, and on one nothing has been posted
 * against: both mean the full charges are still outstanding, which is why this reports the absence
 * rather than substituting zero. Callers that need a figure to display use `resolveBalance` below.
 *
 * Lives outside the presentation layer because the statement-generation bot reads it too.
 *
 * @param invoice The statement to read.
 * @returns The materialized balance, or null/undefined when the statement carries none.
 */
export function getInvoiceBalanceDue(invoice: Invoice): number | null | undefined {
  return invoice.extension?.find((extension) => extension.url === Extensions.BalanceDue)?.valueMoney
    ?.value;
}

/**
 * What the statement still owes, as a figure to display.
 *
 * The materialized extension is authoritative wherever it is present. Where it is absent — a
 * statement written before the extension, or one nothing has been posted against — the balance is
 * the full charges, since nothing has reduced them.
 *
 * Every surface that shows a client or a payer what a statement owes reads this, so the Statements
 * dashboard, the statement detail page and the exported PDF cannot disagree about one statement.
 *
 * @param invoice The statement to read.
 * @returns The balance to display, or undefined when the statement carries no total to fall back on.
 */
export function resolveBalance(invoice: Invoice): number | undefined {
  const materialized = getInvoiceBalanceDue(invoice);
  if (materialized !== null && materialized !== undefined) return materialized;

  return resolveCharges(invoice);
}

/**
 * The amount the statement was issued for: `totalNet`, else `totalGross`.
 *
 * Statement generation writes both with the same value; `totalNet` is read first because it is the
 * figure after any discount. A statement's Charges and its balance before any posting read this, so
 * a fresh statement shows the same figure in both.
 *
 * @param invoice The statement to read.
 * @returns The charges, or undefined when the statement carries neither total.
 */
export function resolveCharges(invoice: Invoice): number | undefined {
  return invoice.totalNet?.value ?? invoice.totalGross?.value;
}

/**
 * A copy of the statement with its materialized balance set to `balanceDue`, replacing any balance
 * already stored.
 *
 * The Ledger postings and the statement-generation bot both write the balance through this one
 * function, so every writer stamps the same extension.
 *
 * @param invoice The statement to copy.
 * @param balanceDue What the statement still owes after the write.
 * @returns The statement carrying exactly one `balance-due` extension.
 */
export function withBalanceDue(invoice: Invoice, balanceDue: number): Invoice {
  const extension = (invoice.extension ?? []).filter((item) => item.url !== Extensions.BalanceDue);

  return {
    ...invoice,
    extension: [
      ...extension,
      { url: Extensions.BalanceDue, valueMoney: { value: balanceDue, currency: "USD" } },
    ],
  };
}
